import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Tooltip } from "antd";
import { TextAreaRef } from "antd/lib/input/TextArea";
import { BulbOutlined, FastForwardOutlined, StopOutlined } from "@ant-design/icons";
import { AICC_SANDBOX_END_REASON, AiccSandboxEndReason } from "constants/aiccPromptSandbox";
import {
  ENDED_BY_GRACE,
  ENDED_BY_SCRIPT,
  HINT,
  HINT_HINT,
  ENDED_BY_MODEL,
  ENDED_GENERIC,
  MESSAGE_PLACEHOLDER,
  SEND_MESSAGE,
  WRAP_UP,
  wrapUpHint,
} from "../configs";
import styles from "../Sandbox.module.scss";

interface Props {
  isEnded: boolean;
  hasHint: boolean;
  hasWrapUp: boolean;
  isHinting: boolean;
  onHint: () => void;
  endReason?: AiccSandboxEndReason;
  isRunning: boolean;
  isBlocked: boolean;
  isWrapUpArmed: boolean;
  isWrapUpSent: boolean;
  onSend: (message: string, isWrapUp?: boolean) => void;
}

const endedNotice = (endReason?: AiccSandboxEndReason) => {
  if (endReason === AICC_SANDBOX_END_REASON.graceExhausted) {
    return ENDED_BY_GRACE;
  }

  if (endReason === AICC_SANDBOX_END_REASON.scriptComplete) {
    return ENDED_BY_SCRIPT;
  }

  return endReason === AICC_SANDBOX_END_REASON.llm ? ENDED_BY_MODEL : ENDED_GENERIC;
};

function ChatComposer({
  isEnded,
  endReason,
  isRunning,
  isHinting,
  hasHint,
  hasWrapUp,
  isBlocked,
  isWrapUpArmed,
  isWrapUpSent,
  onHint,
  onSend,
}: Props) {
  const [message, setMessage] = useState("");
  const [hasSent, setHasSent] = useState(false);
  const inputRef = useRef<TextAreaRef>(null);

  useEffect(() => {
    if (!isRunning && hasSent) {
      setHasSent(false);
      inputRef.current?.focus();
    }
  }, [isRunning, hasSent]);

  const send = (isWrapUp = false) => {
    if (isRunning || isBlocked || !message.trim()) {
      return;
    }

    onSend(message.trim(), isWrapUp);
    setMessage("");
    setHasSent(true);
  };

  if (isEnded) {
    return (
      <div className={styles.ended}>
        <StopOutlined />
        {endedNotice(endReason)}
      </div>
    );
  }

  const sendButton = (
    <Button
      type="primary"
      shape="round"
      className={styles.sendButton}
      disabled={isBlocked || isRunning}
      onClick={() => send()}
    >
      {SEND_MESSAGE}
    </Button>
  );

  const input = (
    <Input.TextArea
      ref={inputRef}
      className={styles.composerInput}
      rows={2}
      value={message}
      placeholder={MESSAGE_PLACEHOLDER}
      onChange={(event) => setMessage(event.target.value)}
      onPressEnter={(event) => {
        if (!event.shiftKey) {
          event.preventDefault();
          send();
        }
      }}
    />
  );

  if (!hasHint && !hasWrapUp) {
    return (
      <div className={styles.composerInline}>
        {input}
        {sendButton}
      </div>
    );
  }

  return (
    <div className={styles.composer}>
      {input}

      <div className={styles.composerActions}>
        {hasHint && (
          <Tooltip title={HINT_HINT}>
            <Button type="text" size="small" icon={<BulbOutlined />} loading={isHinting} onClick={onHint}>
              {HINT}
            </Button>
          </Tooltip>
        )}

        {hasWrapUp && (
          <Tooltip title={wrapUpHint(isWrapUpArmed, isWrapUpSent, message)}>
            <span>
              <Button
                type="text"
                size="small"
                icon={<FastForwardOutlined />}
                disabled={isWrapUpArmed || isWrapUpSent || !message.trim()}
                onClick={() => send(true)}
              >
                {WRAP_UP}
              </Button>
            </span>
          </Tooltip>
        )}

        {sendButton}
      </div>
    </div>
  );
}

export default ChatComposer;
